import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { DisplayToastAction } from 'src/app/core/actions/toast.actions';
import { CloseThread } from 'src/app/messagingV3/legacy/actions/message-ui.actions';
import * as fromRoot from '../../reducers';
import * as fromSettings from '../../settings/reducers';

export const GLOBAL_CMS_ROUTE = 'content/global';

@Injectable()
export class NavigationService {
  private _lastNavigation?: {
    url: string;
    timestamp: number;
    inFlight: boolean;
  };

  private _navigationToastText?: {
    Title?: string;
    AvoidDoubleClickMessage?: string;
  };

  constructor(private _router: Router, private _store: Store<fromRoot.State>) {
    this._store
      .pipe(select(fromSettings.getSectionTranslations('NavigationToast')))
      .subscribe((t) => {
        this._navigationToastText = t;
      });
  }

  navigate(commands: any[], extras?: NavigationExtras) {
    const targetUrl = this._router.serializeUrl(
      this._router.createUrlTree(commands, extras)
    );

    if (this._bypassNavigationEvent(targetUrl)) {
      return;
    }

    if (this._router.url.toLowerCase().startsWith('/messages')) {
      this._store.dispatch(new CloseThread());
    }

    const nav = this._router.navigate(commands, extras);
    this._markNavigationInFlight(targetUrl, nav);
  }

  navigateAfterLogin(isGlobalPortal: boolean) {
    if (isGlobalPortal) {
      this.navigate([`/${GLOBAL_CMS_ROUTE}`]);
    } else {
      this.navigate(['/']);
    }
  }

  navigateByUrl(url: string) {
    const targetUrl = this._router.serializeUrl(this._router.parseUrl(url));

    if (this._bypassNavigationEvent(targetUrl)) {
      return;
    }

    const nav = this._router.navigateByUrl(url);
    this._markNavigationInFlight(targetUrl, nav);
  }

  private _bypassNavigationEvent(targetUrl: string): boolean {
    const last = this._lastNavigation;

    if (!last) {
      return false;
    }

    // If any navigation is currently in-flight, ignore further navigation attempts.
    // Rapid re-entrant navigations (even to different URLs) can wedge router state.
    const isDuplicate = last.inFlight;
    if (isDuplicate && last.url === targetUrl) {
      this._showAvoidDoubleClickToast();
    }

    return isDuplicate;
  }

  private _showAvoidDoubleClickToast() {
    const title = this._navigationToastText?.Title || 'Reminder';
    const message =
      this._navigationToastText?.AvoidDoubleClickMessage ||
      'Please avoid double clicking or clicking too quickly on different links. Click once.';

    this._store.dispatch(
      new DisplayToastAction({
        type: 'info',
        title,
        message,
        timeout: 10000
      })
    );
  }

  private _markNavigationInFlight(
    targetUrl: string,
    navPromise: Promise<boolean>
  ) {
    this._lastNavigation = {
      url: targetUrl,
      timestamp: Date.now(),
      inFlight: true
    };

    navPromise.finally(() => {
      if (this._lastNavigation?.url === targetUrl) {
        this._lastNavigation = {
          url: targetUrl,
          timestamp: this._lastNavigation.timestamp,
          inFlight: false
        };
      }
    });
  }
}
